import React, { ReactElement, useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { PrimaryButton, SecondaryButton } from './Button'
import Radom from '../state/Radom'
import User from '../state/User'
import { TextInputWithLabel } from './Input'
import { errorToast, successToast } from '../util/Util'
import { RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, RADOM_TOKEN_LOCAL_STORAGE_KEY } from '../util/Constants'
import { chunk } from 'lodash'
import LoadingBar from './LoadingBar'

interface Props {
  onTwoFactorSetSuccess: () => void
}

const OneTimePassForm = ({ onTwoFactorSetSuccess }: Props): ReactElement => {
  const [isGeneratingImage, setIsGeneratingImage] = useState(true)
  const [imageData, setImageData] = useState<string>()
  const [token, setToken] = useState<string>()
  const [secret, setSecret] = useState<string>()

  const secretChunks = secret ? chunk(secret, 4) : []

  const verifyOnetimePassword = async(): Promise<void> => {
    if (!token) {
      return
    }

    try {
      await Radom.verifyGeneratedOneTimePassword(token)
      const res = await Radom.verifyOneTimePassword(token)
      Radom.token = res.jwtToken
      localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, res.jwtToken)
      localStorage.setItem(RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, new Date(res.expiresAt).toString())
      onTwoFactorSetSuccess()
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
  }

  const generateOneTimePassword = async(): Promise<void> => {
    setIsGeneratingImage(true)
    await User.getUser()
    const res = await Radom.generateOneTimePassword()

    const data = await QRCode.toDataURL(res.otpAuthUrl)

    setSecret(res.otpBase32)
    setImageData(data)
    setIsGeneratingImage(false)
  }

  useEffect(() => {
    generateOneTimePassword()
  }, [])

  const copySecret = async(): Promise<void> => {
    if (!secret) {
      return
    }

    try {
      await navigator.clipboard.writeText(secret)
      successToast('Setup key copied')
    } catch (err) {
      errorToast(err.message || 'Unable to copy setup key')
    }
  }

  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    alignItems: 'stretch'
  }}>

    <div style={{ fontSize: 14, lineHeight: 1.45, color: '#3b4250' }}>
      Scan this QR code with a security or authentication app like Google Authenticator, Authy or Duo.
      Download one from the App Store or Play Store.
    </div>

    {/* <div style={{ fontSize: 14 }}>
      Once you connect your Radom account to the security or authentication app,
      you&apos;ll get a one-time verification code whenever you need it.
    </div> */}

    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>

      {
        isGeneratingImage
          ? <LoadingBar style={{ width: 180, height: 180, borderRadius: 14 }} />
          : <img
            style={{
              border: '1px solid #edf1f7',
              borderRadius: 14,
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
              width: 180,
              height: 180
            }}
            src={imageData} />
      }

      <span style={{
        fontSize: 11,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: '#7d8492'
      }}>
        Scan QR code or copy setup key
      </span>

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 5,
        fontSize: 13,
        backgroundColor: '#f7f9fc',
        border: '1px solid #edf1f7',
        borderRadius: 8,
        padding: '9px 10px',
        maxWidth: '100%',
        overflowWrap: 'anywhere'
      }}>
        {secretChunks.map((t, i) => <span key={i}>{t}</span>)}
      </div>

      <SecondaryButton
        disabled={!secret}
        onClick={copySecret}
        style={{ fontSize: 13, minHeight: 34, padding: '7px 12px' }}>
        Copy setup key
      </SecondaryButton>
    </div>

    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      width: '100%',
      borderTop: '1px solid #edf1f7',
      paddingTop: 18
    }}>

      <TextInputWithLabel
        label='Enter authentication code'
        required
        autoFocus
        value={token}
        onChange={e => setToken((e.target as HTMLInputElement).value)}
        placeholder='Authentication code' />

      <PrimaryButton onClick={verifyOnetimePassword} style={{ width: '100%' }}>
        Enable 2FA
      </PrimaryButton>
    </div>
  </div>
}

export default OneTimePassForm
